import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import CancelPremiumDialog from "../components/CancelPremiumDialog";
import { openSuggestionBox } from "../components/SuggestionBox";
import { openSongRequestBox } from "../components/SongRequestBox";
import MailPreference from "../components/MailPreference";
import { Sparkles, Check, Pencil } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

const PRICE_TEXT = "$15.99";

// What Premium includes. The same list is shown in the upgrade pop-up (components/UpgradePrompt.js).
const PERKS = [
  "Full-length premium songs",
  "The DJ Booth, to mix your own videos and music on your TV",
  "Favorites and Recently Played",
  "Multiple playlists with 100+ songs",
  "Click-and-drag to reorganize songs in playlists",
  "Request songs from Dan",
  "The Quartz look for buttons and bars",
];

// Shows your display name, with a pencil to change it.
function NameEditor({ user, refresh }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const shown = user.name || user.email;

  const start = () => { setDraft(user.name || ""); setEditing(true); };
  const cancel = () => { setEditing(false); setDraft(""); };
  const save = async () => {
    const name = draft.trim().replace(/\s+/g, " ");
    if (name.length < 2 || name.length > 40) { toast.error("Your name needs to be 2 to 40 characters."); return; }
    if (name === user.name) { cancel(); return; }
    setSaving(true);
    try {
      await api.patch("/auth/me", { name });
      await refresh();
      toast.success("Name updated");
      setEditing(false);
    } catch (e) {
      // A "Premium only" refusal already opens the Premium pop-up by itself.
      if (e.response?.data?.detail !== "premium_required") toast.error(e.response?.data?.detail || "Could not change your name. Please try again.");
    } finally { setSaving(false); }
  };

  if (!editing) return (
    <div className="flex items-center gap-2 min-w-0">
      <div data-testid="profile-name" className="text-2xl font-display font-bold truncate">{shown}</div>
      <button type="button" onClick={start} data-testid="rename-btn" aria-label="Change your name" title="Change your name"
        className="shrink-0 p-2 rounded-full text-zinc-400 hover:text-white hover:bg-white/10"><Pencil className="w-4 h-4" /></button>
    </div>
  );
  return (
    <div className="space-y-2">
      <label htmlFor="rename-input" className="sr-only">Your name</label>
      <input id="rename-input" data-testid="rename-input" value={draft} maxLength={40} autoFocus onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } else if (e.key === "Escape") cancel(); }}
        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-base focus:outline-none focus:border-cyan-400/60" />
      <div className="flex gap-2">
        <button type="button" onClick={save} disabled={saving} data-testid="rename-save"
          className="px-4 py-1.5 rounded-full bg-cyan-400 text-black text-sm font-semibold hover:bg-cyan-300 disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
        <button type="button" onClick={cancel} disabled={saving} data-testid="rename-cancel"
          className="px-4 py-1.5 rounded-full border border-white/20 text-sm hover:bg-white/5">Cancel</button>
      </div>
    </div>
  );
}

export default function Account() {
  const { user, refresh } = useAuth();
  const [sp] = useSearchParams();
  const [cancelOpen, setCancelOpen] = useState(false);   // the "Sorry to see you go" box
  const [starting, setStarting] = useState(false);   // true while we hand the person over to Stripe
  const autoStarted = useRef(false);

  const upgrade = useCallback(async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/subscription/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      setStarting(false);
      // An unconfirmed email already got its own message (see lib/api.js).
      if (e.response?.data?.detail !== "email_not_verified") toast.error("Could not start checkout. Please try again.");
    }
  }, []);

  // The "Sign up for Premium" pop-up sends free members here with ?checkout=1: go straight to Stripe.
  useEffect(() => {
    if (sp.get("checkout") !== "1" || autoStarted.current) return;
    if (!user || user.is_premium || user.role === "admin") return;
    autoStarted.current = true;
    upgrade();
  }, [sp, user, upgrade]);

  useEffect(() => {
    // If just returned from Stripe with session_id, poll status once
    const sid = sp.get("session_id");
    if (sid) {
      const t = setInterval(async () => {
        try {
          const { data } = await api.get(`/subscription/status/${sid}`);
          if (data.payment_status === "paid") { clearInterval(t); toast.success("Welcome to Premium!"); refresh(); }
        } catch (err) { void err; }
      }, 2500);
      return () => clearInterval(t);
    }
  }, [sp, refresh]);

  if (!user) return (
    <div className="py-20 text-center">
      <h1 className="font-display text-3xl mb-3">Sign in to manage your account</h1>
      <Link to="/login" className="text-cyan-400 hover:underline">Log in</Link>
    </div>
  );

  const isAdmin = user.role === "admin";
  const hasPremium = user.is_premium || isAdmin;
  const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  // The box (components/CancelPremiumDialog.js) asks why and sends the cancel; this runs once it worked.
  const canceled = (data) => {
    setCancelOpen(false);
    toast.success(data.ends_at ? `Premium will end on ${fmtDate(data.ends_at)}` : "Premium canceled");
    refresh();
  };

  return (
    <div className="max-w-4xl">
      <CancelPremiumDialog open={cancelOpen} onClose={() => setCancelOpen(false)} onCanceled={canceled} />
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold mb-8">Account</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="min-w-0 bg-zinc-900/60 border border-white/10 rounded-2xl p-6">
          <div className="font-mono-ui text-xs uppercase tracking-widest text-zinc-500 mb-2">Profile</div>
          <NameEditor user={user} refresh={refresh} />
          {/* A long address wraps (after the @ first) instead of running out of the card on a phone. */}
          <div data-testid="profile-email" className="text-zinc-400 break-words">
            {user.email.includes("@") ? <>{user.email.split("@")[0]}@<wbr />{user.email.split("@").slice(1).join("@")}</> : user.email}
          </div>
          <div className="mt-3 text-xs font-mono-ui uppercase tracking-widest text-cyan-400">{user.role}</div>
        </div>
        <div className={`min-w-0 rounded-2xl p-6 border ${hasPremium ? "border-amber-400/40 bg-gradient-to-br from-amber-500/10 to-crimson/10" : "border-cyan-400/30 bg-cyan-400/5"}`}>
          <div className="flex items-center gap-2 font-mono-ui text-xs uppercase tracking-widest text-cyan-300 mb-2">
            <Sparkles className="w-3.5 h-3.5" /> {hasPremium ? "Premium Active" : "Dan The Man TV Premium"}
          </div>
          <div className="font-display text-3xl font-extrabold mb-3">$15.99 <span className="text-base text-zinc-400 font-medium">/ month</span></div>
          <ul className="space-y-2 text-sm text-zinc-300 mb-6" data-testid="premium-perks">
            {PERKS.map((perk) => (
              <li key={perk} className="flex gap-2"><Check className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" /> {perk}</li>
            ))}
          </ul>
          {isAdmin ? (
            <div data-testid="admin-premium-note" className="rounded-xl border border-white/15 bg-white/5 p-3 text-sm text-zinc-300">
              Admin accounts always have Premium, so there is nothing to subscribe to or cancel here. To try the upgrade and cancel buttons, log in as a regular test account.
            </div>
          ) : user.is_premium && user.premium_comp ? (
            <div className="space-y-3">
              <div data-testid="premium-comp-note" className="rounded-xl border border-white/15 bg-white/5 p-3 text-sm text-zinc-300">
                {user.premium_comp === "complimentary"
                  ? "Your Premium is complimentary from Dan The Man TV. Enjoy!"
                  : <>Your free {user.premium_comp === "trial" ? "7-day trial" : "month"} of Premium runs until <b>{fmtDate(user.premium_until)}</b>. After that your account goes back to free.</>}
              </div>
              {user.premium_comp !== "complimentary" && (
                <button data-testid="comp-subscribe-btn" onClick={upgrade} disabled={starting} className="w-full py-2.5 rounded-full bg-cyan-400 text-black font-semibold hover:bg-cyan-300 disabled:opacity-60">{starting ? "Opening Stripe…" : "Subscribe to keep Premium"}</button>
              )}
            </div>
          ) : user.is_premium && user.premium_cancel_at ? (
            <div data-testid="premium-ends-note" className="rounded-xl border border-white/15 bg-white/5 p-3 text-sm text-zinc-300">
              Premium ends on <b>{fmtDate(user.premium_cancel_at)}</b>. It will not renew. You can sign up again any time after that.
            </div>
          ) : user.is_premium ? (
            <button data-testid="cancel-premium-btn" onClick={() => setCancelOpen(true)} className="w-full py-2.5 rounded-full border border-white/20 hover:bg-white/5">Cancel Premium</button>
          ) : (
            <button data-testid="upgrade-premium-btn" onClick={upgrade} disabled={starting} className="w-full py-2.5 rounded-full bg-cyan-400 text-black font-semibold hover:bg-cyan-300 disabled:opacity-60">{starting ? "Opening Stripe…" : "Upgrade to Premium"}</button>
          )}
          {/* Shown wherever someone could start paying, so the terms of the subscription are clear first. */}
          {!isAdmin && !(user.is_premium && !user.premium_comp) && (
            <p data-testid="billing-note" className="mt-3 text-xs text-zinc-500">
              {PRICE_TEXT} per month, plus sales tax where it applies, billed until you cancel. Cancel any time here and keep Premium until the end of the month you have paid for. Paid months are not refunded. By subscribing you agree to the <Link to="/terms" className="text-cyan-400 hover:underline">Terms</Link>.
            </p>
          )}
        </div>
      </div>
      <div className="mt-6">
        <MailPreference />
      </div>
      <div className="mt-6 text-sm text-zinc-400">
        Something you'd like to see, or something that's not working?{" "}
        <button type="button" onClick={openSuggestionBox} data-testid="account-suggest" className="text-cyan-400 hover:underline">Send Dan a suggestion</button>
        {" · "}
        <button type="button" onClick={openSongRequestBox} data-testid="account-song-request" className="text-cyan-400 hover:underline">Request a song</button>
        <span className="text-zinc-500"> (Premium)</span>
      </div>
    </div>
  );
}
